import { useEffect, useMemo, useState } from 'react';
import type { StationMeta } from '@tideprecision/shared';
import { apiGetStations } from '../api.js';
import { haversineKm } from '../lib/distance.js';
import { prefectureOrder } from '../lib/prefectures.js';
import { useStore } from '../state/store.js';

interface Props {
  /** 選択時に呼ばれる */
  onSelect: (station: StationMeta) => void;
}

// 「近くの地点」セクションの抽出半径 (km)。
// spec 006 参照: 全 239 地点の集計で最大 28 件、中央値 10 件に収まる。
const NEARBY_RADIUS_KM = 100;

interface StationWithDistance {
  station: StationMeta;
  distanceKm: number;
}

export function StationPicker({ onSelect }: Props): JSX.Element {
  const [stations, setStations] = useState<StationMeta[]>([]);
  const [query, setQuery] = useState('');
  const favorites = useStore((s) => s.favorites);
  const selectedStation = useStore((s) => s.selectedStation);
  const addFavorite = useStore((s) => s.addFavorite);
  const removeFavorite = useStore((s) => s.removeFavorite);

  useEffect(() => {
    let cancelled = false;
    apiGetStations()
      .then((r) => {
        if (!cancelled) setStations(r.stations);
      })
      .catch(() => {
        // オフライン等の理由で失敗したら IndexedDB のキャッシュを使う想定（T7 で実装）
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 検索フィルタ: 名前・カナ・都道府県のいずれかに部分一致
  const matchesQuery = (s: StationMeta, q: string): boolean =>
    s.name.includes(q) ||
    (s.name_kana ?? '').includes(q) ||
    (s.prefecture ?? '').includes(q);

  const { favStations, nearbyStations, otherGroups, otherTotal, favSet } = useMemo(() => {
    const q = query.trim();
    const favSet = new Set(favorites);

    // 「近くの地点」候補集合 (お気に入り・自分自身を除いた、100km 以内)
    // selectedStation がない場合は空集合のまま。
    const nearbySet = new Set<number>();
    const nearby: StationWithDistance[] = [];
    if (selectedStation) {
      for (const s of stations) {
        if (s.id === selectedStation.id) continue;
        if (favSet.has(s.id)) continue;
        const d = haversineKm(
          selectedStation.latitude,
          selectedStation.longitude,
          s.latitude,
          s.longitude,
        );
        if (d <= NEARBY_RADIUS_KM) {
          nearby.push({ station: s, distanceKm: d });
          nearbySet.add(s.id);
        }
      }
      nearby.sort((a, b) => a.distanceKm - b.distanceKm);
    }

    // 「お気に入り」と「すべての地点」は別役割（ショートカット vs 網羅リスト）として
    // 重複表示を許容する。これは「すべての地点」で県の中から探したいユーザの動線を
    // 壊さないため。
    const inFav: StationMeta[] = [];
    const all: StationMeta[] = [];
    for (const s of stations) {
      if (q && !matchesQuery(s, q)) continue;
      if (favSet.has(s.id)) inFav.push(s);
      all.push(s);
    }
    // お気に入りは登録順（store の配列順）
    inFav.sort((a, b) => favorites.indexOf(a.id) - favorites.indexOf(b.id));

    // 検索クエリは近くの地点にも適用する
    const filteredNearby = q
      ? nearby.filter((n) => matchesQuery(n.station, q))
      : nearby;

    // 「すべての地点」を都道府県でグルーピング → 北→南順に並べる。
    // 県内の並びは API から来た順を維持する（=Map の挿入順）。
    // 近くの地点・お気に入りに出ている地点もここに重複して載せる。
    const groupMap = new Map<string, StationMeta[]>();
    for (const s of all) {
      const key = s.prefecture ?? 'その他';
      const list = groupMap.get(key);
      if (list) list.push(s);
      else groupMap.set(key, [s]);
    }
    const groups = [...groupMap.entries()].sort(
      ([a], [b]) => prefectureOrder(a) - prefectureOrder(b),
    );

    return {
      favStations: inFav,
      nearbyStations: filteredNearby,
      otherGroups: groups,
      otherTotal: all.length,
      favSet,
    };
  }, [query, stations, favorites, selectedStation]);

  const total = favStations.length + nearbyStations.length + otherTotal;

  return (
    <div className="space-y-2">
      <input
        type="search"
        placeholder="地点を検索 (例: 博多 / カゴシマ / 福岡県)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 placeholder:text-slate-500"
      />
      <div className="max-h-72 overflow-y-auto rounded border border-slate-800">
        {total === 0 ? (
          <div className="p-3 text-sm text-slate-500">地点が見つかりません</div>
        ) : (
          <>
            {favStations.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-xs font-bold text-yellow-300/80 bg-slate-900 sticky top-0 z-10">
                  ⭐ お気に入り ({favStations.length})
                </div>
                <ul className="divide-y divide-slate-800">
                  {favStations.map((s) =>
                    renderRow(s, true, undefined, onSelect, addFavorite, removeFavorite),
                  )}
                </ul>
              </>
            )}
            {nearbyStations.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-xs font-bold text-sky-300/80 bg-slate-900 sticky top-0 z-10">
                  📍 近くの地点 ({nearbyStations.length})
                </div>
                <ul className="divide-y divide-slate-800">
                  {nearbyStations.map((n) =>
                    renderRow(
                      n.station,
                      false,
                      n.distanceKm,
                      onSelect,
                      addFavorite,
                      removeFavorite,
                    ),
                  )}
                </ul>
              </>
            )}
            {otherTotal > 0 && (
              <>
                {(favStations.length > 0 || nearbyStations.length > 0) && (
                  <div className="px-3 py-1.5 text-xs font-bold text-emerald-300/80 bg-slate-900 sticky top-0 z-10">
                    🗾 すべての地点 ({otherTotal})
                  </div>
                )}
                {otherGroups.map(([pref, group]) => (
                  <div key={pref}>
                    <div className="px-3 py-1 text-[11px] font-semibold text-emerald-200/70 bg-slate-800 border-t border-slate-700/60">
                      {pref}（{group.length}）
                    </div>
                    <ul className="divide-y divide-slate-800">
                      {group.map((s) =>
                        renderRow(s, favSet.has(s.id), undefined, onSelect, addFavorite, removeFavorite),
                      )}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function renderRow(
  s: StationMeta,
  fav: boolean,
  distanceKm: number | undefined,
  onSelect: (s: StationMeta) => void,
  addFavorite: (id: number) => void,
  removeFavorite: (id: number) => void,
): JSX.Element {
  return (
    <li
      key={s.id}
      className="flex items-center justify-between px-3 py-2 hover:bg-slate-800/60"
    >
      <button type="button" onClick={() => onSelect(s)} className="flex-1 text-left min-w-0">
        <div className="text-sm font-bold">{s.name}</div>
        <div className="text-xs text-slate-400">
          {s.prefecture ?? ''} ({s.latitude.toFixed(3)}, {s.longitude.toFixed(3)})
        </div>
      </button>
      {distanceKm !== undefined && (
        <span className="text-xs text-sky-300/80 tabular-nums mr-2 shrink-0">
          {distanceKm.toFixed(1)} km
        </span>
      )}
      <button
        type="button"
        onClick={() => (fav ? removeFavorite(s.id) : addFavorite(s.id))}
        className={`text-lg ${fav ? 'text-yellow-400' : 'text-slate-600 hover:text-slate-400'}`}
        aria-label={fav ? 'お気に入りから外す' : 'お気に入りに追加'}
      >
        ★
      </button>
    </li>
  );
}
